// @flow
/* global window,document */
import _ from 'lodash';
import { parse } from 'url';
import type { UserType } from '../types/user';

// isBrowser is a function for testing purposes
const isBrowser = () => typeof window !== 'undefined';
let localAmplitude: any;
let dataLayer: Array<any> = isBrowser() && window.dataLayer ? window.dataLayer : [];

export function callAmplitudeFn(functionName: string, ...args: Array<any>) {
  const amp = isBrowser() ? window.amplitude : localAmplitude;
  const instance = amp && amp.getInstance();

  if (instance && typeof instance[functionName] === 'function') {
    instance[functionName](...args);
  }
}

// I think we should try to move towards using
// the models from the DB directly instead of relying
// on the transform that the user API does on the user model
// So look for user_id first
export function getUserId(user: UserType): string {
  return user.user_id || user.oid;
}

export class Tracking {
  // client and node mixpanel api is the same
  // so this allows for mixpanel to be passed in when there is no window object
  static init(amplitudeApi: any, gaDataLayer?: Array<any>) {
    localAmplitude = amplitudeApi;
    dataLayer = gaDataLayer || dataLayer;
  }

  static registerUser(user: UserType) {
    const id = getUserId(user);
    const { email } = user;

    callAmplitudeFn('setUserId', id);
    Tracking.setPeopleProps({
      $id: id,
      $email: email,
      $name: email,
      $user_type: 'Stride User',
      $id_type: 'user_id',
    });
  }

  static registerBrokerAgent(agentId: string, email: string) {
    callAmplitudeFn('setUserId', agentId);
    Tracking.setPeopleProps({
      $id: agentId,
      $user_type: 'Broker Agent',
      $email: email,
      $id_type: 'agent_id',
    }, true);
  }

  static registerEnterpriseConsumer(personId: string, email: string, extraProperties?: Object) {
    callAmplitudeFn('setUserId', personId);
    Tracking.setPeopleProps({
      $id: personId,
      $user_type: 'Enterprise Consumer',
      $email: email,
      $id_type: 'person_id',
    }, true);

    if (extraProperties) {
      Tracking.setPeopleProps(extraProperties);
    }
  }

  static identify(user: UserType) {
    Tracking.registerUser(user);
  }

  static alias(user: UserType) {
    Tracking.registerUser(user);
  }

  static track(name: string, properties: Object = {}) {
    callAmplitudeFn('logEvent', name, {
      // Added current_url for Amplitude https://trello.com/c/FMHw26Sy/190-replicate-the-currenturl-functionality-in-amplitude
      current_url: isBrowser() ? window.location.href : '',
      ...properties, // let passed in current_url take priority
    });
  }

  static trackGtmEvent(name: string, properties: Object = {}) {
    dataLayer.push({
      event: name,
      current_url: isBrowser() ? window.location.href : '',
      ...properties,
    });
  }

  static trackPageView(path: string = '', name: string = '', url: string = '') {
    const pageview_name = name || (isBrowser() ? document.title : '');
    const pageview_url = path || (isBrowser() ? window.location.pathname + window.location.search : '');

    // Added current_url for Amplitude https://trello.com/c/FMHw26Sy/190-replicate-the-currenturl-functionality-in-amplitude
    const current_url = url || (isBrowser() ? window.location.href : '');
    const site = Tracking.getSite(url);
    const ref = Tracking.getRefCode(url);
    const userId = (isBrowser() ? _.get(window, '__INITIAL_STATE__.user.oid', '') : '');
    const payload: Object = {
      site,
      event: 'virtualPageView',
      virtualUrl: pageview_url,
    };

    if (ref) payload.ref = ref;
    if (!_.isEmpty(userId)) payload.userId = userId;

    const application_id = (isBrowser() ? _.get(window, '__INITIAL_STATE__.applicationId', '') : '');
    const agency_code = (isBrowser() ? _.get(window, '__INITIAL_STATE__.siteConfigs.partnerAgencyInfo.agencyInfo.agencyCode', '') : '');
    const application_type = (isBrowser() ? _.get(window, '__INITIAL_STATE__.applicationInfo.applicationType', '') : '');

    const extra_properties = {};
    if (!_.isEmpty(application_id)) extra_properties.application_id = application_id;
    if (!_.isEmpty(agency_code)) extra_properties.agency_code = agency_code;
    if (!_.isEmpty(application_type)) extra_properties.application_type = application_type;

    dataLayer.push(payload);
    Tracking.track('Page View', { pageview_name, pageview_url, current_url, ...extra_properties });
  }


  static setPeopleProps(props: Object, setOnce: boolean = false) {
    if (setOnce) {
      Tracking.callAmplitudeIdentifyFn('setOnce', { ...props });
    } else {
      Tracking.callAmplitudeIdentifyFn('set', { ...props });
    }
  }

  static clearCookie() {
    callAmplitudeFn('setUserId', null);
    callAmplitudeFn('regenerateDeviceId');
  }

  static getSite(urlString?: string): string {
    const url = urlString || (isBrowser() && window.location.href) || '';
    const hostname = parse(url).hostname || '';
    let site = hostname.split('.')[0];

    // if we're on stridehealth.com directly, manually set to www
    // otherwise, we can default to the first value whether it's sidecar, dev, www, or localhost
    if (!site || site === 'stridehealth') {
      site = 'www';
    }

    return site;
  }

  static getRefCode(urlString?: string): string {
    const url = urlString || (isBrowser() && window.location.href) || '';
    const mediumKey = 'utm_medium';
    const contentKey = 'utm_content';
    // $FlowFixMe
    const queryParams: Object = parse(url, true).query;
    let ref = '';

    // look for the ref parameter in the querystring first
    if (queryParams.ref && queryParams.ref.length) {
      ref = queryParams.ref;
    } else if (queryParams[mediumKey] && queryParams[mediumKey].length) {
      // if that's empty, try looking for utm keys
      ref = `${queryParams[mediumKey]}-${queryParams.utm_source}-${queryParams.utm_campaign}`;

      if (queryParams[contentKey] && queryParams[contentKey].length) {
        ref += `-${queryParams[contentKey]}`;
      }
    }

    return ref;
  }

  static callAmplitudeIdentifyFn(
    functionName: string,
    props: Object,
    mockIdentifyInstance?: Object,
  ) {
    const amp = isBrowser() ? window.amplitude : localAmplitude;
    const identifyInstance = mockIdentifyInstance || (amp && new amp.Identify());

    if (identifyInstance && typeof identifyInstance[functionName] === 'function') {
      Object.keys(props)
        .forEach(key => identifyInstance[functionName](key, props[key]));

      amp.getInstance().identify(identifyInstance);
    }
  }

  static add(name: string, value: number) {
    Tracking.callAmplitudeIdentifyFn('add', {
      [name]: value,
    });
  }
}

export const events: {[x:string]: string} = {
  PURCHASE_COMPLETE: 'Purchase Complete',
};
